import type { CurvaDocument } from "@/document/model/document";
import { documentStore } from "@/editor/store/documentStore";
import { selectionStore } from "@/editor/store/selectionStore";
import { uiStore } from "@/editor/store/uiStore";
import { viewStore } from "@/editor/store/viewStore";
import { StoreSubject } from "@/lib/store/StoreSubject";
import type { Camera } from "@/renderer/camera";
import { Renderer } from "@/renderer/Renderer";
import type { RenderOverlay } from "@/renderer/overlayTypes";
import {
  markRenderRequestScheduled,
  recordBuildOverlayMs,
  recordControllerFlushStart,
  setRenderTelemetryTrigger,
} from "@/renderer/renderTelemetry";
import { overlayRequiresFullScene, type RenderMode } from "@/renderer/sceneComposition";
import { wrapRafCallback } from "@/renderer/renderDebug";
import type { RenderOverlayOverrides } from "@/editor/canvas/renderOverlay";

export type OverlayOverrides = RenderOverlayOverrides;

export interface CanvasRenderOverlayContext {
  camera: Camera;
  document: CurvaDocument;
  overrides?: OverlayOverrides;
}

export type BuildRenderOverlayFn = (context: CanvasRenderOverlayContext) => RenderOverlay;

export interface CanvasRenderControllerOptions {
  canvas: HTMLCanvasElement;
  renderer?: Renderer;
  rendererOptions?: { alpha?: boolean; preserveDrawingBuffer?: boolean };
  buildOverlay: BuildRenderOverlayFn;
  /** When true, always render the full scene immediately. */
  forceFullScene?: () => boolean;
  onOverlayBuilt?: (overlay: RenderOverlay) => void;
  /** Subscribe to documentStore + viewStore and schedule renders. Off until shell state migrates. */
  subscribeToStores?: boolean;
}

interface PendingRenderRequest {
  mode?: RenderMode;
  immediate?: boolean;
  overrides?: OverlayOverrides;
  trigger?: string;
}

function subscribeSkipInitial<T>(subject: StoreSubject<T>, listener: (value: T) => void): () => void {
  let isInitial = true;
  return subject.subscribe(value => {
    if (isInitial) {
      isInitial = false;
      return;
    }
    listener(value);
  });
}

/**
 * Owns the main canvas Renderer and coalesces render requests into a single RAF tick.
 * Store subscriptions are optional until EditorShell / CanvasWorkspace write to the stores.
 */
export class CanvasRenderController {
  private readonly buildOverlay: BuildRenderOverlayFn;
  private readonly forceFullScene?: () => boolean;
  private readonly onOverlayBuilt?: (overlay: RenderOverlay) => void;
  private readonly renderer: Renderer;
  private readonly ownsRenderer: boolean;
  private readonly unsubscribers: Array<() => void> = [];
  private scheduleFrameId: number | undefined;
  private pendingRequest: PendingRenderRequest | undefined;
  private lastOverlay: RenderOverlay;

  constructor(options: CanvasRenderControllerOptions) {
    this.buildOverlay = options.buildOverlay;
    this.forceFullScene = options.forceFullScene;
    this.onOverlayBuilt = options.onOverlayBuilt;
    this.ownsRenderer = options.renderer === undefined;
    this.renderer = options.renderer ?? new Renderer(options.canvas, options.rendererOptions);
    this.lastOverlay = this.buildOverlaySnapshot();

    if (options.subscribeToStores) {
      this.attachStoreSubscriptions();
    }
  }

  getRenderer(): Renderer {
    return this.renderer;
  }

  getLastOverlay(): RenderOverlay {
    return this.lastOverlay;
  }

  attachStoreSubscriptions(): void {
    if (this.unsubscribers.length > 0) {
      return;
    }

    const schedule = (request: PendingRenderRequest = {}): void => {
      this.pendingRequest = {
        ...this.pendingRequest,
        ...request,
        mode: request.mode ?? this.pendingRequest?.mode,
        immediate: request.immediate ?? this.pendingRequest?.immediate,
        overrides: request.overrides ?? this.pendingRequest?.overrides,
        trigger: request.trigger ?? this.pendingRequest?.trigger,
      };
      markRenderRequestScheduled(this.pendingRequest.trigger ?? "store");
      this.scheduleRender();
    };

    this.unsubscribers.push(
      subscribeSkipInitial(documentStore.document, () => {
        const document = documentStore.getDocument();
        this.renderer.noteDocumentChange(documentStore.getDocumentChange(document));
        schedule({ mode: "full", trigger: "store:document" });
      }),
      subscribeSkipInitial(viewStore.camera, () => {
        schedule({ mode: "camera", trigger: "store:camera" });
      }),
      subscribeSkipInitial(viewStore.hoveredNodeId, () => {
        schedule({ trigger: "store:hover" });
      }),
      subscribeSkipInitial(viewStore.previews, () => {
        schedule({ mode: "full", trigger: "store:previews" });
      }),
      subscribeSkipInitial(viewStore.vertexPreview, () => {
        schedule({ mode: "full", trigger: "store:vertexPreview" });
      }),
      subscribeSkipInitial(viewStore.handlePreview, () => {
        schedule({ mode: "full", trigger: "store:handlePreview" });
      }),
      subscribeSkipInitial(viewStore.edgeBendPreview, () => {
        schedule({ mode: "full", trigger: "store:edgeBendPreview" });
      }),
      subscribeSkipInitial(viewStore.sculptBrushPreview, () => {
        schedule({ mode: "full", trigger: "store:sculptBrushPreview" });
      }),
      subscribeSkipInitial(viewStore.vectorTopologyPreview, () => {
        schedule({ mode: "full", trigger: "store:vectorTopologyPreview" });
      }),
      subscribeSkipInitial(viewStore.textPreviews, () => {
        schedule({ mode: "full", trigger: "store:textPreviews" });
      }),
      subscribeSkipInitial(viewStore.boundsPreview, () => {
        schedule({ mode: "full", trigger: "store:boundsPreview" });
      }),
      subscribeSkipInitial(viewStore.fontsReadyToken, token => {
        if (token === 0) {
          return;
        }

        this.renderer.invalidateTextRendering();
        this.renderer.invalidateAll(documentStore.getDocument());
        schedule({ mode: "full", trigger: "store:fontsReady" });
      }),
      subscribeSkipInitial(selectionStore.selection, () => {
        schedule({ mode: "full", trigger: "store:selection" });
      }),
      subscribeSkipInitial(selectionStore.tool, () => {
        schedule({ trigger: "store:tool" });
      }),
      subscribeSkipInitial(selectionStore.freshCutVertexIds, () => {
        schedule({ mode: "full", trigger: "store:freshCutVertices" });
      }),
      (() => {
        // Redraw when vector-targeting mode toggles, even if the node selection
        // is unchanged (e.g. pressing Enter on the already-selected shape).
        // Guarded so this does not schedule on every animation playback tick.
        let lastVectorTargetNodeId = uiStore.animation.getValue().vectorTargetNodeId;
        return subscribeSkipInitial(uiStore.animation, () => {
          const next = uiStore.animation.getValue().vectorTargetNodeId;
          if (next === lastVectorTargetNodeId) {
            return;
          }
          lastVectorTargetNodeId = next;
          schedule({ mode: "full", trigger: "store:vectorTarget" });
        });
      })(),
      subscribeSkipInitial(uiStore.colorSampler, () => {
        schedule({ mode: "full", immediate: true, trigger: "store:colorSampler" });
      }),
      subscribeSkipInitial(uiStore.editingImageFillTarget, () => {
        schedule({ mode: "full", trigger: "store:imageFillGizmo" });
      }),
      subscribeSkipInitial(uiStore.editingGradientFillTarget, () => {
        schedule({ mode: "full", trigger: "store:gradientGizmo" });
      }),
      subscribeSkipInitial(uiStore.canvasDebug, () => {
        schedule({ mode: "full", trigger: "store:canvasDebug" });
      }),
      subscribeSkipInitial(uiStore.rightPanelTab, () => {
        schedule({ trigger: "store:rightPanelTab" });
      }),
      subscribeSkipInitial(uiStore.selectedPrototypeLinkId, () => {
        schedule({ trigger: "store:prototypeLink" });
      }),
    );
  }

  detachStoreSubscriptions(): void {
    for (const unsubscribe of this.unsubscribers) {
      unsubscribe();
    }
    this.unsubscribers.length = 0;
  }

  dispose(): void {
    this.detachStoreSubscriptions();
    if (this.scheduleFrameId !== undefined) {
      cancelAnimationFrame(this.scheduleFrameId);
      this.scheduleFrameId = undefined;
    }
    this.pendingRequest = undefined;
    if (this.ownsRenderer) {
      this.renderer.dispose();
    }
  }

  setCamera(camera: Camera): void {
    this.renderer.setCamera(camera);
  }

  setTileZoomBucket(bucket: number): void {
    this.renderer.setTileZoomBucket(bucket);
  }

  requestRender(mode: RenderMode = "full", options: { immediate?: boolean; trigger?: string } = {}): void {
    this.pendingRequest = {
      ...this.pendingRequest,
      mode,
      immediate: options.immediate ?? this.pendingRequest?.immediate,
      trigger: options.trigger ?? this.pendingRequest?.trigger,
    };
    markRenderRequestScheduled(this.pendingRequest.trigger ?? "requestRender");
    this.scheduleRender();
  }

  syncOverlayRender(overrides?: OverlayOverrides, options: { immediate?: boolean; trigger?: string } = {}): void {
    const overlay = this.buildOverlaySnapshot(overrides);
    const mode = this.resolveMode(overlay);
    const request: PendingRenderRequest = {
      mode,
      immediate: options.immediate,
      overrides,
      trigger: options.trigger ?? "overlay",
    };

    if (options.immediate) {
      if (this.scheduleFrameId !== undefined) {
        cancelAnimationFrame(this.scheduleFrameId);
        this.scheduleFrameId = undefined;
      }
      this.pendingRequest = undefined;
      markRenderRequestScheduled(request.trigger ?? "overlay");
      this.flushRender(request);
      return;
    }

    this.pendingRequest = request;
    markRenderRequestScheduled(request.trigger ?? "overlay");
    this.scheduleRender();
  }

  renderNow(mode?: RenderMode, overrides?: OverlayOverrides, options: { trigger?: string } = {}): void {
    if (this.scheduleFrameId !== undefined) {
      cancelAnimationFrame(this.scheduleFrameId);
      this.scheduleFrameId = undefined;
    }
    this.pendingRequest = undefined;
    markRenderRequestScheduled(options.trigger ?? "renderNow");
    this.flushRender({ mode, immediate: true, overrides, trigger: options.trigger ?? "renderNow" });
  }

  /** Renders a caller-built overlay without rebuilding from the overlay provider. */
  presentScene(
    overlay: RenderOverlay,
    mode: RenderMode = "full",
    options: { immediate?: boolean } = {},
  ): void {
    this.lastOverlay = overlay;
    this.onOverlayBuilt?.(overlay);

    const document = documentStore.getDocument();
    const camera = viewStore.getCamera();
    this.renderer.setCamera(camera);

    const forceFull = this.forceFullScene?.() ?? false;
    const resolvedMode: RenderMode = forceFull ? "full" : mode;

    if (options.immediate || forceFull) {
      this.renderer.drawImmediate(document, overlay, resolvedMode);
      return;
    }

    this.renderer.scheduleDraw(document, overlay, resolvedMode);
  }

  invalidateAll(document: CurvaDocument): void {
    this.renderer.invalidateAll(document);
  }

  invalidateTextRendering(): void {
    this.renderer.invalidateTextRendering();
  }

  stabilizeAfterCameraGesture(): void {
    this.renderer.stabilizeAfterCameraGesture();
  }

  setProfilerEnabled(enabled: boolean): void {
    this.renderer.setProfilerEnabled(enabled);
  }

  setProfilerOverlayVisible(visible: boolean): void {
    this.renderer.setProfilerOverlayVisible(visible);
  }

  setRenderTrianglesDebug(enabled: boolean): void {
    this.renderer.setRenderTrianglesDebug(enabled);
  }

  private scheduleRender(): void {
    if (this.scheduleFrameId !== undefined) {
      return;
    }

    this.scheduleFrameId = requestAnimationFrame(() => {
      wrapRafCallback("raf:controller:flush", () => {
        this.scheduleFrameId = undefined;
        const request = this.pendingRequest;
        this.pendingRequest = undefined;
        if (request) {
          this.flushRender(request);
        }
      });
    });
  }

  private flushRender(request: PendingRenderRequest): void {
    recordControllerFlushStart();
    const overlayStart = performance.now();
    const overlay = this.buildOverlaySnapshot(request.overrides);
    recordBuildOverlayMs(performance.now() - overlayStart);
    if (request.trigger) {
      setRenderTelemetryTrigger(request.trigger);
    }
    this.lastOverlay = overlay;
    this.onOverlayBuilt?.(overlay);

    const document = documentStore.getDocument();
    const camera = viewStore.getCamera();
    this.renderer.setCamera(camera);

    const resolvedMode = request.mode ?? this.resolveMode(overlay);
    const forceFull = this.forceFullScene?.() ?? false;
    const mode: RenderMode = forceFull ? "full" : resolvedMode;

    if (request.immediate || forceFull || mode === "camera") {
      this.renderer.drawImmediate(document, overlay, mode);
      return;
    }

    this.renderer.scheduleDraw(document, overlay, mode);
  }

  private buildOverlaySnapshot(overrides?: OverlayOverrides): RenderOverlay {
    return this.buildOverlay({
      camera: viewStore.getCamera(),
      document: documentStore.getDocument(),
      overrides,
    });
  }

  private resolveMode(overlay: RenderOverlay): RenderMode {
    return overlayRequiresFullScene(overlay) ? "full" : "camera";
  }
}
